import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getSaleById, getSaleItems, getCustomerById } from '@/lib/supabaseDb';

interface DeliveryReportProps {
  delivery: any;
  onClose: () => void;
}

function formatTRY(amount: number): string {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(amount).replace('₺', '₺');
}

function formatDate(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
}

function todayTR(): string {
  const d = new Date();
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function hasElectronics(items: any[]): boolean {
  const keywords = ['beyaz eşya', 'buzdolabı', 'çamaşır', 'bulaşık', 'fırın', 'tv', 'televizyon', 'klima', 'elektrikli', 'elektronik', 'profilo'];
  return (items || []).some((item: any) => {
    const cat = ((item.category || item.product_category || '') + ' ' + (item.product_name || '')).toLowerCase();
    return keywords.some(k => cat.includes(k));
  });
}

function hasFurniture(items: any[]): boolean {
  const keywords = ['mobilya', 'koltuk', 'masa', 'sandalye', 'yatak', 'dolap', 'sehpa', 'komodin', 'kitaplık', 'berjer', 'kanep'];
  return (items || []).some((item: any) => {
    const cat = ((item.category || item.product_category || '') + ' ' + (item.product_name || '')).toLowerCase();
    return keywords.some(k => cat.includes(k));
  });
}

function DeliveryReportContent({ delivery, sale, customer, items }: {
  delivery: any;
  sale: any;
  customer: any;
  items: any[];
}) {
  const isElectronics = hasElectronics(items);
  const isFurniture = hasFurniture(items);

  const deliveryDate = delivery?.delivery_date || '';
  const deliveryTime = delivery?.delivery_start_time || delivery?.delivery_time || '';
  const deliveryPerson = delivery?.delivery_person || '';
  const deliveryAddress = delivery?.address || sale?.delivery_address || '';
  const deliveryNotes = delivery?.notes || '';
  const reportDate = deliveryDate ? formatDate(deliveryDate + 'T00:00:00') : todayTR();

  return (
    <>
      <div style={{ borderBottom: '3px double #1a1a1a', paddingBottom: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, margin: '0 0 4px 0', letterSpacing: '-0.5px' }}>POLATLAR MOBİLYA</h1>
            <p style={{ fontSize: '14px', margin: '0 0 2px 0', color: '#444' }}>Mobilya ve Profilo Ürün Teslim Tutanağı</p>
            <p style={{ fontSize: '11px', margin: '0', color: '#666' }}>Bu tutanak, ürün teslimatı sırasında taraflarca kontrol edilerek imza altına alınmak üzere hazırlanmıştır.</p>
          </div>
          <div style={{ textAlign: 'right', fontSize: '12px', color: '#555' }}>
            <p style={{ margin: '0 0 4px 0' }}><strong>Tarih:</strong> {reportDate}</p>
            <p style={{ margin: '0 0 4px 0' }}><strong>Teslimat No:</strong> {delivery?.id ? delivery.id.slice(0, 8) : '—'}</p>
            <p style={{ margin: '0' }}><strong>Satış No:</strong> {sale?.sale_no || delivery?.sale_no || '—'}</p>
          </div>
        </div>
      </div>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 10px 0', paddingBottom: '6px', borderBottom: '1px solid #1a1a1a', textTransform: 'uppercase', letterSpacing: '1px' }}>Müşteri Bilgileri</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <tbody>
            <tr><td style={{ padding: '4px 8px', width: '50%', fontWeight: 500, color: '#555' }}>Müşteri Adı Soyadı:</td><td style={{ padding: '4px 8px', width: '50%' }}>{customer?.full_name || delivery?.customer_name || '—'}</td></tr>
            <tr><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>Müşteri No:</td><td style={{ padding: '4px 8px' }}>{customer?.customer_no || '—'}</td></tr>
            <tr><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>Telefon:</td><td style={{ padding: '4px 8px' }}>{customer?.phone || delivery?.customer_phone || '—'}</td></tr>
            {customer?.second_phone && (<tr><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>2. Telefon:</td><td style={{ padding: '4px 8px' }}>{customer.second_phone}</td></tr>)}
            <tr><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>Teslimat Adresi:</td><td style={{ padding: '4px 8px' }}>{deliveryAddress || customer?.address || '—'}</td></tr>
            <tr><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>Mahalle:</td><td style={{ padding: '4px 8px' }}>{delivery?.neighborhood || customer?.neighborhood || '—'}</td></tr>
            <tr><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>İlçe:</td><td style={{ padding: '4px 8px' }}>{delivery?.district || customer?.district || '—'}</td></tr>
            <tr><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>Teslimat Tarihi:</td><td style={{ padding: '4px 8px' }}>{deliveryDate ? formatDate(deliveryDate + 'T00:00:00') : '—'}</td></tr>
            <tr><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>Teslimat Saati:</td><td style={{ padding: '4px 8px' }}>{deliveryTime || '—'}</td></tr>
          </tbody>
        </table>
      </div>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 10px 0', paddingBottom: '6px', borderBottom: '1px solid #1a1a1a', textTransform: 'uppercase', letterSpacing: '1px' }}>Teslim Edilen Ürünler</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #1a1a1a', backgroundColor: '#f5f5f5' }}>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600, width: '30px' }}>Sıra</th>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600 }}>Ürün Cinsi</th>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600 }}>Marka</th>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600 }}>Ürün Adı</th>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600 }}>Model Kodu</th>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600 }}>Renk</th>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600 }}>Ölçü</th>
              <th style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 600, width: '40px' }}>Adet</th>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600 }}>Açıklama</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? items.map((item: any, idx: number) => (
              <tr key={item.id || idx} style={{ borderBottom: '1px solid #ddd' }}>
                <td style={{ padding: '5px 6px', textAlign: 'center' }}>{idx + 1}</td>
                <td style={{ padding: '5px 6px' }}>{item.product_category || item.category || '—'}</td>
                <td style={{ padding: '5px 6px' }}>{item.brand || '—'}</td>
                <td style={{ padding: '5px 6px' }}>{item.product_name || '—'}</td>
                <td style={{ padding: '5px 6px', fontSize: '10px' }}>{item.model_code || '—'}</td>
                <td style={{ padding: '5px 6px' }}>{item.color || '—'}</td>
                <td style={{ padding: '5px 6px' }}>{item.size || item.measurement || '—'}</td>
                <td style={{ padding: '5px 6px', textAlign: 'center' }}>{item.quantity || 1}</td>
                <td style={{ padding: '5px 6px', fontSize: '10px' }}>{item.description || item.notes || '—'}</td>
              </tr>
            )) : (
              <tr><td colSpan={9} style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #ddd', color: '#999' }}>{delivery?.product_name || 'Ürün bilgisi bulunamadı'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ marginBottom: '20px', padding: '12px 14px', border: '1px solid #ccc', backgroundColor: '#fafafa', borderRadius: '4px' }}>
        <p style={{ fontSize: '12px', margin: '0 0 8px 0', lineHeight: '1.5' }}>Yukarıda bilgileri yer alan ürün/ürünler, müşteri tarafından kontrol edilerek sağlam, eksiksiz ve çalışır durumda teslim alınmıştır. Teslimat sonrası ürünlerin fiziki durumu müşteri tarafından onaylanmıştır.</p>
        {isElectronics && (<p style={{ fontSize: '12px', margin: '0 0 4px 0', lineHeight: '1.5', fontWeight: 500 }}>Elektronik/beyaz eşya ürünlerinde kutu, model ve seri bilgileri müşteri tarafından kontrol edilmiştir.</p>)}
        {isFurniture && (<p style={{ fontSize: '12px', margin: '0', lineHeight: '1.5', fontWeight: 500 }}>Mobilya ürünlerinde renk, model, ölçü ve adet bilgileri müşteri tarafından kontrol edilmiştir.</p>)}
      </div>
      <div style={{ marginBottom: '20px', padding: '10px 14px', border: '1px solid #ccc', borderRadius: '4px' }}>
        <h3 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 8px 0' }}>Ürün Sağlam Teslim Onayı</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'default' }}><span style={{ display: 'inline-block', width: '16px', height: '16px', border: '1.5px solid #333', borderRadius: '2px', flexShrink: 0 }}></span>Ürün/ürünler sağlam teslim edildi.</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'default' }}><span style={{ display: 'inline-block', width: '16px', height: '16px', border: '1.5px solid #333', borderRadius: '2px', flexShrink: 0 }}></span>Ürün/ürünler eksiksiz teslim edildi.</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'default' }}><span style={{ display: 'inline-block', width: '16px', height: '16px', border: '1.5px solid #333', borderRadius: '2px', flexShrink: 0 }}></span>Müşteri teslimat adresinde ürünü kontrol etti.</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'default' }}><span style={{ display: 'inline-block', width: '16px', height: '16px', border: '1.5px solid #333', borderRadius: '2px', flexShrink: 0 }}></span>Teslimat sırasında gözle görülür hasar bulunmamaktadır.</label>
        </div>
      </div>
      {deliveryNotes && (
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 6px 0', paddingBottom: '4px', borderBottom: '1px solid #999' }}>Teslimat Notu</h2>
          <p style={{ fontSize: '12px', margin: '0', padding: '8px', backgroundColor: '#f9f9f9', border: '1px solid #eee', borderRadius: '4px', minHeight: '30px' }}>{deliveryNotes}</p>
        </div>
      )}
      <div style={{ marginTop: '30px', display: 'flex', gap: '40px' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 16px 0', borderBottom: '1px solid #ccc', paddingBottom: '8px' }}>TESLİM EDEN</h3>
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <tbody>
              <tr><td style={{ padding: '3px 4px', fontWeight: 500, color: '#555', width: '60px' }}>Ad Soyad:</td><td style={{ padding: '3px 4px', borderBottom: '1px dotted #999' }}>{deliveryPerson}</td></tr>
              <tr><td style={{ padding: '3px 4px', fontWeight: 500, color: '#555' }}>Telefon:</td><td style={{ padding: '3px 4px', borderBottom: '1px dotted #999' }}></td></tr>
              <tr><td style={{ padding: '3px 4px', fontWeight: 500, color: '#555' }}>Tarih:</td><td style={{ padding: '3px 4px', borderBottom: '1px dotted #999' }}></td></tr>
            </tbody>
          </table>
          <div style={{ marginTop: '20px', height: '60px', borderBottom: '1px solid #333', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: '4px' }}><span style={{ fontSize: '11px', color: '#999' }}>İmza</span></div>
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 16px 0', borderBottom: '1px solid #ccc', paddingBottom: '8px' }}>TESLİM ALAN</h3>
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <tbody>
              <tr><td style={{ padding: '3px 4px', fontWeight: 500, color: '#555', width: '60px' }}>Ad Soyad:</td><td style={{ padding: '3px 4px', borderBottom: '1px dotted #999' }}>{customer?.full_name || delivery?.customer_name || ''}</td></tr>
              <tr><td style={{ padding: '3px 4px', fontWeight: 500, color: '#555' }}>Telefon:</td><td style={{ padding: '3px 4px', borderBottom: '1px dotted #999' }}>{customer?.phone || delivery?.customer_phone || ''}</td></tr>
              <tr><td style={{ padding: '3px 4px', fontWeight: 500, color: '#555' }}>Tarih:</td><td style={{ padding: '3px 4px', borderBottom: '1px dotted #999' }}></td></tr>
            </tbody>
          </table>
          <div style={{ marginTop: '20px', height: '60px', borderBottom: '1px solid #333', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: '4px' }}><span style={{ fontSize: '11px', color: '#999' }}>İmza</span></div>
        </div>
      </div>
      <div style={{ marginTop: '30px', paddingTop: '12px', borderTop: '1px solid #ddd', textAlign: 'center' }}>
        <p style={{ fontSize: '10px', color: '#999', margin: '0' }}>Bu tutanak, ürün teslimatı sırasında taraflarca kontrol edilerek imza altına alınmak üzere hazırlanmıştır.</p>
        <p style={{ fontSize: '10px', color: '#aaa', margin: '4px 0 0 0' }}>Polatlar Mobilya — Teslimat No: {delivery?.id ? delivery.id.slice(0, 8) : '—'} — {reportDate}</p>
      </div>
    </>
  );
}

export default function DeliveryReport({ delivery, onClose }: DeliveryReportProps) {
  const reportRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const [sale, setSale] = useState<any>(null);
  const [customer, setCustomer] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);

  // Create portal container at body level (shared with receipt print root)
  useEffect(() => {
    let rootEl = document.getElementById('receipt-print-root');
    if (!rootEl) {
      rootEl = document.createElement('div');
      rootEl.id = 'receipt-print-root';
      rootEl.style.display = 'none';
      document.body.appendChild(rootEl);
    }
    portalRef.current = rootEl as HTMLDivElement;
  }, []);

  useEffect(() => {
    async function load() {
      if (!delivery?.sale_id) return;
      const [saleRes, itemsRes] = await Promise.all([
        getSaleById(delivery.sale_id),
        getSaleItems(delivery.sale_id),
      ]);
      setSale(saleRes.data);
      setItems(itemsRes.data || []);
      const cid = delivery?.customer_id || saleRes.data?.customer_id;
      if (cid) {
        const custRes = await getCustomerById(cid);
        setCustomer(custRes.data);
      }
    }
    load();
  }, [delivery]);

  const handlePrint = useCallback(() => {
    window.dispatchEvent(new CustomEvent('receipt-before-print'));
    setTimeout(() => {
      window.print();
    }, 150);
  }, []);

  const deliveryDate = delivery?.delivery_date || '';
  const deliveryTime = delivery?.delivery_start_time || delivery?.delivery_time || '';
  const reportDate = deliveryDate ? formatDate(deliveryDate + 'T00:00:00') : todayTR();

  const hasData = sale || customer || items.length > 0;

  // Portal content - rendered at body level for print CSS to find it
  const printPortal = portalRef.current
    ? createPortal(
        <div id="receipt-print-area" className="receipt-report" style={{ maxWidth: '190mm', width: '100%', margin: '0 auto', padding: '15mm 15mm', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '13px', lineHeight: '1.5', color: '#1a1a1a', background: 'white' }}>
          {hasData ? (
            <DeliveryReportContent delivery={delivery} sale={sale} customer={customer} items={items} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
              <p style={{ color: '#999' }}>Teslimat bilgileri yükleniyor...</p>
            </div>
          )}
        </div>,
        portalRef.current
      )
    : null;

  return (
    <>
      {printPortal}

      <div className="fixed inset-0 bg-black/40 z-[200] flex items-start justify-center overflow-y-auto py-8">
        <div className="absolute inset-0 no-print" onClick={onClose}></div>
        <div className="relative bg-background-50 rounded-2xl shadow-2xl w-full max-w-[900px] mx-4 my-4 delivery-modal-card" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-background-200 sticky top-0 bg-background-50 z-10 rounded-t-2xl no-print">
            <div><h2 className="text-lg font-bold text-foreground-900 font-heading">Teslimat Tutanağı</h2><p className="text-xs text-foreground-400">Yazdırılabilir A4 rapor</p></div>
            <div className="flex items-center gap-2">
              <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-printer-line"></i>Yazdır</button>
              <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-background-50 rounded-lg text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-2-line"></i>PDF Olarak Kaydet</button>
              <button onClick={onClose} className="w-9 h-9 rounded-lg border border-background-200 flex items-center justify-center cursor-pointer hover:bg-background-100 text-foreground-400"><i className="ri-close-line"></i></button>
            </div>
          </div>
          <div className="overflow-y-auto max-h-[calc(100vh-180px)] p-4 bg-background-100 delivery-scroll-area">
            <div id="delivery-print-area" ref={reportRef} className="delivery-report bg-white mx-auto" style={{ maxWidth: '210mm', minHeight: '297mm', padding: '15mm 20mm', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '13px', lineHeight: '1.6', color: '#1a1a1a' }}>
              {hasData ? (
                <DeliveryReportContent delivery={delivery} sale={sale} customer={customer} items={items} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
                  <p style={{ color: '#999' }}>Teslimat bilgileri yükleniyor...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}